import React from 'react';
import type { Stream } from '../types/Stream';
import StreamerService from '../services/StreamerService';
import { 
  FaUsers, 
  FaClock, 
  FaGamepad, 
  FaTwitch, 
  FaPlus, 
  FaCheck,
  FaExternalLinkAlt
} from 'react-icons/fa';
import { SiKick } from 'react-icons/si';

interface StreamCardProps {
  stream: Stream;
  isSelected?: boolean;
  onSelect?: (stream: Stream) => void;
  multiviewEnabled?: boolean;
}

const StreamCard: React.FC<StreamCardProps> = ({ 
  stream, 
  isSelected = false, 
  onSelect, 
  multiviewEnabled = false 
}) => {
  const formatViewerCount = (count: number): string => {
    return StreamerService.formatViewerCount(count);
  };

  const getStreamDuration = (startedAt: string): string => {
    return StreamerService.getStreamDuration(startedAt);
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'twitch':
        return <FaTwitch className="platform-icon-svg" />;
      case 'kick':
        return <SiKick className="platform-icon-svg" />;
      default:
        return <FaGamepad className="platform-icon-svg" />;
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Empêcher la navigation si on clique sur le bouton de sélection
    if ((e.target as HTMLElement).closest('.multiview-select-btn')) {
      return;
    }
    window.open(stream.streamUrl, '_blank', 'noopener,noreferrer');
  };

  const handleMultiviewSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect(stream);
    }
  };

  const handleThumbnailError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const target = e.target as HTMLImageElement;
    target.style.display = 'none';
    target.nextElementSibling?.classList.remove('hidden');
  };

  return (
    <div className="stream-card fade-in" onClick={handleCardClick}>
      <div className="stream-thumbnail-container">
        <img
          src={stream.thumbnailUrl}
          alt={`${stream.displayName} stream thumbnail`}
          className="stream-thumbnail"
          onError={handleThumbnailError}
        />
        <div className="stream-thumbnail-placeholder hidden">
          <FaGamepad className="thumbnail-icon" />
          <span className="thumbnail-text">Aperçu indisponible</span>
        </div>
        
        <div className="stream-overlay">
          <div className="stream-live-badge">
            <span className="live-indicator"></span>
            LIVE
          </div>
          <div className="stream-stats">
            <div className="stream-viewer-count">
              <FaUsers /> {formatViewerCount(stream.viewerCount)}
            </div>
            <div className="stream-duration">
              <FaClock /> {getStreamDuration(stream.startedAt)}
            </div>
          </div>
        </div>
      </div>

      <div className="stream-info">
        <div className="stream-header">
          <div className="streamer-info">
            <img
              src={stream.avatarUrl}
              alt={`${stream.displayName} avatar`}
              className="streamer-avatar"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGNpcmNsZSBjeD0iMjAiIGN5PSIyMCIgcj0iMjAiIGZpbGw9InJnYmEoNTUsIDk5LCA2MywgMC41KSIvPgo8dGV4dCB4PSIyMCIgeT0iMjYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IndoaXRlIiBmb250LWZhbWlseT0ic2Fucy1zZXJpZiIgZm9udC1zaXplPSIyMCI+8J+OrTwvdGV4dD4KICA8L3N2Zz4=';
              }}
            />
            <div className="streamer-details">
              <h3 className="streamer-name">{stream.displayName}</h3>
              <div className="platform-info">
                {getPlatformIcon(stream.platform)}
                <span className="platform-name">{stream.platform}</span>
              </div>
            </div>
            {multiviewEnabled && (
              <button
                className={`multiview-select-btn ${isSelected ? 'selected' : ''}`}
                onClick={handleMultiviewSelect}
                title={isSelected ? 'Retirer du multiview' : 'Ajouter au multiview'}
              >
                {isSelected ? <FaCheck /> : <FaPlus />}
              </button>
            )}
          </div>
        </div>

        <div className="stream-content">
          <h4 className="stream-title" title={stream.title}>
            {stream.title}
          </h4>
          <div className="stream-game">
            <FaGamepad /> {stream.gameName}
          </div>
          
          <button 
            className="external-link-btn"
            onClick={(e) => {
              e.stopPropagation();
              window.open(stream.streamUrl, '_blank', 'noopener,noreferrer');
            }}
            title="Ouvrir le stream"
          >
            <FaExternalLinkAlt /> Regarder
          </button>
        </div>
      </div>
    </div>
  );
};

export default StreamCard;